import { useState, useEffect, useMemo } from 'react';
import { Row, Col, Form, Button, Collapse } from 'react-bootstrap';
import Select, { components } from 'react-select';
import DatePicker from 'react-datepicker';
import isEqual from 'lodash/isEqual';
import debounce from 'lodash.debounce';
import { defaultFilters } from '../../components/Reporting/defaultFilters';


export default function FilterBar( { filters, setFilters, reportConfig, dynamicOptions } ) {
    const [ showAdvanced, setShowAdvanced ] = useState( false );
    const [ localMinPurchase, setLocalMinPurchase ] = useState( filters.minPurchasePrice || '' );
    const [ localMaxPurchase, setLocalMaxPurchase ] = useState( filters.maxPurchasePrice || '' );
    const [ localMinAmount, setLocalMinAmount ] = useState( filters.minLoanAmount || '' );
    const [ localMaxAmount, setLocalMaxAmount ] = useState( filters.maxLoanAmount || '' );
    const [ localMinRate, setLocalMinRate ] = useState( filters.minInterestRate || '' );
    const [ localMaxRate, setLocalMaxRate ] = useState( filters.maxInterestRate || '' );


    const debouncedSetFilters = useMemo( () => {
        return debounce( ( field, value ) => {
            setFilters( ( prev ) => ( {
                ...prev,
                [field]: value,
            } ) );
        }, 400 );
    }, [ setFilters ] );

    const handleMultiSelect = (field, selected) => {
        setFilters({ ...filters, [field]: selected.map((s) => s.value) });
    };

    const CustomMultiValue = (props) => {
        const { index, data, selectProps } = props;
        const maxToShow = 2;
        const selectedCount = selectProps.value.length;
      
        if (index < maxToShow) {
            return <components.MultiValue {...props} />;
        }
        if (index === maxToShow) {
            return (
                <div className="react-select__multi-value">
                    <div className="react-select__multi-value__label">
                        +{selectedCount - maxToShow} more
                    </div>
                </div>
            );
        }
        return null;
    };
      
    const CustomMultiValueRemove = () => null;
    
    const hasModifiedFilters = ( filters, reportId ) => {
        return !isEqual( filters, defaultFilters() );
    }

    useEffect( () => {
        console.log( 'options: ', dynamicOptions );
    }, [ dynamicOptions ] );

    useEffect( () => {
        const handler = setTimeout( () => {
            if ( localMinPurchase !== filters.minPurchasePrice ) {
                setFilters( ( prev ) => ( {
                    ...prev,
                    minPurchasePrice: localMinPurchase,
                } ) );
            }
        }, 400 ); // debounce delay
      
        return () => clearTimeout( handler );
    }, [ localMinPurchase, filters.minPurchasePrice, setFilters ] );

    useEffect( () => {
        setLocalMinPurchase( filters.minPurchasePrice || '' );
    }, [ filters.minPurchasePrice ] );

    useEffect( () => {
        const handler = setTimeout( () => {
            if ( localMaxPurchase !== filters.maxPurchasePrice ) {
                setFilters( ( prev ) => ( {
                    ...prev,
                    maxPurchasePrice: localMaxPurchase,
                } ) );
            }
        }, 400 ); // debounce delay
      
        return () => clearTimeout( handler );
    }, [ localMaxPurchase, filters.maxPurchasePrice, setFilters ] );
    
    useEffect( () => {
        setLocalMaxPurchase( filters.maxPurchasePrice || '' );
    }, [ filters.maxPurchasePrice ] );

    useEffect( () => {
        const handler = setTimeout( () => {
            if ( localMinAmount !== filters.minLoanAmount ) {
                setFilters( ( prev ) => ( {
                    ...prev,
                    minLoanAmount: localMinAmount,
                } ) );
            }
        }, 400 ); // debounce delay
      
        return () => clearTimeout( handler );
    }, [ localMinAmount, filters.minLoanAmount, setFilters ] );

    useEffect( () => {
        setLocalMinAmount( filters.minLoanAmount || '' );
    }, [ filters.minLoanAmount ] );

    useEffect( () => {
        const handler = setTimeout( () => {
            if ( localMaxAmount !== filters.maxLoanAmount ) {
                setFilters( ( prev ) => ( {
                    ...prev,
                    maxLoanAmount: localMaxAmount,
                } ) );
            }
        }, 400 ); // debounce delay
      
        return () => clearTimeout( handler );
    }, [ localMaxAmount, filters.maxLoanAmount, setFilters ] );

    useEffect( () => {
        setLocalMaxAmount( filters.maxLoanAmount || '' );
    }, [ filters.maxLoanAmount ] );

    useEffect( () => {
        const handler = setTimeout( () => {
            if ( localMinRate !== filters.minInterestRate ) {
                setFilters( ( prev ) => ( {
                    ...prev,
                    minInterestRate: localMinRate,
                } ) );
            }
        }, 400 ); // debounce delay
      
        return () => clearTimeout( handler );
    }, [ localMinRate, filters.minInterestRate, setFilters ] );

    useEffect( () => {
        setLocalMinRate( filters.minInterestRate || '' );
    }, [ filters.minInterestRate ] );

    useEffect( () => {
        const handler = setTimeout( () => {
            if ( localMaxRate !== filters.maxInterestRate ) {
                setFilters( ( prev ) => ( {
                    ...prev,
                    maxInterestRate: localMaxRate,
                } ) );
            }
        }, 400 ); // debounce delay
      
        return () => clearTimeout( handler );
    }, [ localMaxRate, filters.maxInterestRate, setFilters ] );

    useEffect( () => {
        setLocalMaxRate( filters.maxInterestRate || '' );
    }, [ filters.maxInterestRate ] );

    useEffect(() => {
        return () => {
          debouncedSetFilters.cancel();
        };
    }, [ debouncedSetFilters ] );

    return (
        <Form className="p-3 border rounded">
        {/* Row 1: Dates + Loan Attributes */}
        <Row className="g-2 mb-3">
            {reportConfig.filters.includes('dateRange') && (
            <Col md={ filters.dateRange === 'custom' ? 1 : 2 }>
                <Form.Label>Date Range</Form.Label>
                <Form.Select
                    size="sm"
                    value={filters.dateRange}
                    onChange={(e) =>
                        setFilters({ ...filters, dateRange: e.target.value })
                    }
                >
                    <option value="last30days">Last 30 Days</option>
                    <option value="last90days">Last 90 Days</option>
                    <option value="last180days">Last 180 Days</option>
                    <option value="ytd">Year to Date</option>
                    <option value="custom">Custom</option>
                </Form.Select>
            </Col>
            )}

            {filters.dateRange === 'custom' && (
            <>
                <Col md={2}>
                    <Form.Label>Start Date</Form.Label>
                    <div className="form-control form-control-sm">
                        <DatePicker
                            selected={filters.startDate}
                            onChange={(date) => setFilters({ ...filters, startDate: date })}
                            placeholderText="Start"
                            className="border-0"
                        />
                    </div>
                </Col>
                <Col md={2}>
                    <Form.Label>End Date</Form.Label>
                    <div className="form-control form-control-sm">
                        <DatePicker
                            selected={filters.endDate}
                            onChange={(date) => setFilters({ ...filters, endDate: date })}
                            placeholderText="End"
                            className="border-0"
                        />
                    </div>
                </Col>
            </>
            )}

            {reportConfig.filters.includes('loanPurpose') && (
            <Col md={2}>
                <Form.Label>Loan Purpose</Form.Label>
                <Select
                    isMulti
                    options={dynamicOptions.loanPurpose}
                    placeholder="Purpose"
                    onChange={(selected) => handleMultiSelect('loanPurpose', selected)}
                    value={filters.loanPurpose.map((v) => ({
                        value: v,
                        label: v.charAt(0).toUpperCase() + v.slice(1),
                    }))}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    components={{
                        MultiValue: CustomMultiValue,
                        MultiValueRemove: CustomMultiValueRemove,
                    }}
                    styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '31px',
                          height: '31px',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '0 6px',
                        }),
                        indicatorsContainer: (base) => ({
                          ...base,
                          height: '31px',
                        }),
                    }}
                />
            </Col>
            )}

            {reportConfig.filters.includes('loanType') && (
            <Col md={2}>
                <Form.Label>Loan Type</Form.Label>
                <Select
                    isMulti
                    options={dynamicOptions.loanType}
                    placeholder="Type"
                    onChange={(selected) => handleMultiSelect('loanType', selected)}
                    value={dynamicOptions.loanType.filter((opt) =>
                        filters.loanType.includes(opt.value)
                    )}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    components={{
                        MultiValue: CustomMultiValue,
                        MultiValueRemove: CustomMultiValueRemove,
                    }}
                    styles={{
                        control: (base) => ({
                          ...base,
                          minHeight: '31px',
                          height: '31px',
                        }),
                        valueContainer: (base) => ({
                          ...base,
                          padding: '0 6px',
                        }),
                        indicatorsContainer: (base) => ({
                          ...base,
                          height: '31px',
                        }),
                    }}
                />
            </Col>
            )}

            {reportConfig.filters.includes('loanSource') && (
            <Col md={2}>
                <Form.Label>Loan Source</Form.Label>
                <Select
                    isMulti
                    options={dynamicOptions.loanSource}
                    placeholder="Loan Source"
                    onChange={(selected) =>
                        handleMultiSelect('loanSource', selected)
                    }
                    value={dynamicOptions.loanSource.filter((opt) =>
                        filters.loanSource.includes(opt.value)
                    )}
                    className="react-select-container"
                    classNamePrefix="react-select"
                    components={{
                        MultiValue: CustomMultiValue,
                        MultiValueRemove: CustomMultiValueRemove,
                    }}
                    styles={{
                        control: (base) => ({
                            ...base,
                            minHeight: '31px',
                            height: '31px',
                        }),
                        valueContainer: (base) => ({
                            ...base,
                            padding: '0 6px',
                        }),
                        indicatorsContainer: (base) => ({
                            ...base,
                            height: '31px',
                        }),
                    }}
                />
            </Col>
            )}
        </Row>

        {/* Advanced Filters Collapse */}
        <Collapse in={showAdvanced}>
            <div>
                <Row className="g-2 mb-3">
                    {reportConfig.filters.includes('referralSource') && (
                    <Col md={2}>
                        <Form.Label>Referred By</Form.Label>
                        <Select
                            isMulti
                            options={dynamicOptions.referralSource}
                            placeholder="Referral Source"
                            onChange={(selected) =>
                                handleMultiSelect('referralSource', selected)
                            }
                            value={dynamicOptions.referralSource.filter((opt) =>
                                filters.referralSource.includes(opt.value)
                            )}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            components={{
                                MultiValue: CustomMultiValue,
                                MultiValueRemove: CustomMultiValueRemove,
                            }}
                            styles={{
                                control: (base) => ({
                                ...base,
                                minHeight: '31px',
                                height: '31px',
                                }),
                                valueContainer: (base) => ({
                                ...base,
                                padding: '0 6px',
                                }),
                                indicatorsContainer: (base) => ({
                                ...base,
                                height: '31px',
                                }),
                            }}
                        />
                    </Col>
                    )}
                </Row>
                <Row className="g-2 mb-3">
                {reportConfig.filters.includes('city') && (
                    <Col md={2}>
                        <Form.Label>City</Form.Label>
                        <Select
                            isMulti
                            options={dynamicOptions.cities}
                            placeholder="City"
                            onChange={(selected) => handleMultiSelect('city', selected)}
                            value={dynamicOptions.cities.filter((opt) =>
                                filters.city.includes(opt.value)
                            )}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            components={{
                                MultiValue: CustomMultiValue,
                                MultiValueRemove: CustomMultiValueRemove,
                            }}
                            styles={{
                                control: (base) => ({
                                ...base,
                                minHeight: '31px',
                                height: '31px',
                                }),
                                valueContainer: (base) => ({
                                ...base,
                                padding: '0 6px',
                                }),
                                indicatorsContainer: (base) => ({
                                ...base,
                                height: '31px',
                                }),
                            }}
                        />
                    </Col>
                    )}

                    {reportConfig.filters.includes('state') && (
                    <Col md={2}>
                        <Form.Label>State</Form.Label>
                        <Select
                            isMulti
                            options={dynamicOptions.states}
                            placeholder="State"
                            onChange={(selected) => handleMultiSelect('state', selected)}
                            value={dynamicOptions.states.filter((opt) =>
                                filters.state.includes(opt.value)
                            )}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            components={{
                                MultiValue: CustomMultiValue,
                                MultiValueRemove: CustomMultiValueRemove,
                            }}
                            styles={{
                                control: (base) => ({
                                ...base,
                                minHeight: '31px',
                                height: '31px',
                                }),
                                valueContainer: (base) => ({
                                ...base,
                                padding: '0 6px',
                                }),
                                indicatorsContainer: (base) => ({
                                ...base,
                                height: '31px',
                                }),
                            }}
                        />
                    </Col>
                    )}

                    {reportConfig.filters.includes('zip') && (
                    <Col md={2}>
                        <Form.Label>Zip</Form.Label>
                        <Select
                            isMulti
                            options={dynamicOptions.zips}
                            placeholder="Zip"
                            onChange={(selected) => handleMultiSelect('zip', selected)}
                            value={dynamicOptions.zips.filter((opt) =>
                                filters.zip.includes(opt.value)
                            )}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            components={{
                                MultiValue: CustomMultiValue,
                                MultiValueRemove: CustomMultiValueRemove,
                            }}
                            styles={{
                                control: (base) => ({
                                ...base,
                                minHeight: '31px',
                                height: '31px',
                                }),
                                valueContainer: (base) => ({
                                ...base,
                                padding: '0 6px',
                                }),
                                indicatorsContainer: (base) => ({
                                ...base,
                                height: '31px',
                                }),
                            }}
                        />
                    </Col>
                    )}

                    {reportConfig.filters.includes('county') && (
                    <Col md={2}>
                        <Form.Label>County</Form.Label>
                        <Select
                            isMulti
                            options={dynamicOptions.counties}
                            placeholder="County"
                            onChange={(selected) => handleMultiSelect('county', selected)}
                            value={dynamicOptions.counties.filter((opt) =>
                                filters.county.includes(opt.value)
                            )}
                            className="react-select-container"
                            classNamePrefix="react-select"
                            components={{
                                MultiValue: CustomMultiValue,
                                MultiValueRemove: CustomMultiValueRemove,
                            }}
                            styles={{
                                control: (base) => ({
                                ...base,
                                minHeight: '31px',
                                height: '31px',
                                }),
                                valueContainer: (base) => ({
                                ...base,
                                padding: '0 6px',
                                }),
                                indicatorsContainer: (base) => ({
                                ...base,
                                height: '31px',
                                }),
                            }}
                        />
                    </Col>
                    )}
                </Row>
                <Row className="g-2 mb-3">
                    {reportConfig.filters.includes('minPurchasePrice') && (
                    <>
                        <Col md={2}>
                            <Form.Label>Min Purchase $</Form.Label>
                            <Form.Control
                                type="number"
                                size="sm"
                                value={localMinPurchase}
                                onChange={ ( e ) => setLocalMinPurchase( e.target.value ) }
                            />
                        </Col>
                        <Col md={2}>
                            <Form.Label>Max Purchase $</Form.Label>
                            <Form.Control
                                type="number"
                                size="sm"
                                value={localMaxPurchase}
                                onChange={ ( e ) => setLocalMaxPurchase( e.target.value ) }
                            />
                        </Col>
                        <Col md={1}></Col>
                    </>
                    )}

                    {reportConfig.filters.includes('minLoanAmount') && (
                    <>
                        <Col md={2}>
                            <Form.Label>Min Loan $</Form.Label>
                            <Form.Control
                                type="number"
                                size="sm"
                                value={localMinAmount}
                                onChange={ ( e ) => setLocalMinAmount( e.target.value ) }
                            />
                        </Col>
                        <Col md={2}>
                            <Form.Label>Max Loan $</Form.Label>
                            <Form.Control
                                type="number"
                                size="sm"
                                value={localMaxAmount}
                                onChange={ ( e ) => setLocalMaxAmount( e.target.value ) }
                            />
                        </Col>
                        <Col md={1}></Col>
                    </>
                    )}
                </Row>

                {reportConfig.filters.includes('minInterestRate') && (
                    <Row className="g-2 mb-3">
                        {/* Interest Rate Min/Max */}
                        <Col md={2}>
                            <Form.Label>Min Interest Rate (%)</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                size="sm"
                                value={localMinRate}
                                onChange={ ( e ) => setLocalMinRate( e.target.value ) }
                            />
                        </Col>

                        <Col md={2}>
                            <Form.Label>Max Interest Rate (%)</Form.Label>
                            <Form.Control
                                type="number"
                                min="0"
                                step="0.01"
                                size="sm"
                                value={localMaxRate}
                                onChange={ ( e ) => setLocalMaxRate( e.target.value ) }
                            />
                        </Col>
                    </Row>
                )}

                {reportConfig.filters.includes('conversionMilestones') && (
                    <Row className="g-2 mb-3">
                        <Col md={2}>
                            <Form.Label>Conversion Milestones</Form.Label>
                            <Select
                                isMulti
                                options={dynamicOptions.conversionMilestones}
                                placeholder="Conversion Milestones"
                                onChange={(selected) =>
                                    handleMultiSelect('conversionMilestones', selected)
                                }
                                value={dynamicOptions.conversionMilestones.filter((opt) =>
                                    filters.conversionMilestones.includes(opt.value)
                                )}
                                className="react-select-container"
                                classNamePrefix="react-select"
                                components={{
                                    MultiValue: CustomMultiValue,
                                    MultiValueRemove: CustomMultiValueRemove,
                                }}
                                styles={{
                                    control: (base) => ({
                                        ...base,
                                        minHeight: '31px',
                                        height: '31px',
                                    }),
                                    valueContainer: (base) => ({
                                        ...base,
                                        padding: '0 6px',
                                    }),
                                    indicatorsContainer: (base) => ({
                                        ...base,
                                        height: '31px',
                                    }),
                                }}
                            />
                        </Col>
                    </Row>
                )}
            </div>
        </Collapse>

        <Row>
            <Col md="auto" className="d-flex align-items-start">
                <Button
                    variant="link"
                    size="sm"
                    className="filter-toggle text-decoration-none"
                    onClick={() => setShowAdvanced((prev) => !prev)}
                >
                    {showAdvanced ? '▲ Less Filters' : '▼ More Filters'}
                </Button>
                { hasModifiedFilters( filters, reportConfig.id ) && (
                    <Button
                        variant="link"
                        size="sm"
                        className="filter-toggle text-decoration-none"
                        onClick={() => setFilters(defaultFilters(reportConfig.id))}
                    >
                        Reset Filters
                    </Button>
                ) }
            </Col>
        </Row>
        </Form>
    );
}